import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import PageHeader from '../components/ui/PageHeader.jsx';
import StatusBadge from '../components/ui/StatusBadge.jsx';
import ClientForm from './ClientForm.jsx';

export default function ClientsPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [clientType, setClientType] = useState('');
  const [page, setPage] = useState(1);

  const qs = new URLSearchParams();
  if (search) qs.set('q', search);
  if (status) qs.set('status', status);
  if (clientType) qs.set('clientType', clientType);
  qs.set('page', page);
  qs.set('pageSize', 20);
  qs.set('sortBy', 'updated_at');
  qs.set('sortOrder', 'desc');

  const { data, loading, error, refresh } = useApi('/clients?' + qs.toString(), {
    deps: [search, status, clientType, page],
  });

  const clients = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    refresh();
  }

  return (
    <>
      <PageHeader
        title="Clienti"
        subtitle={data ? `${data.total} clienti in anagrafica` : 'Anagrafica clienti'}
        action={
          <button className="btn btn-primary" onClick={() => { setEditingId(null); setShowForm(true); }}>
            + Nuovo cliente
          </button>
        }
      />

      {/* Barra filtri */}
      <div className="card-elevated mb-4 flex flex-wrap items-center gap-3">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Cerca per nome, email, P.IVA, CF..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="input w-auto" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Tutti gli stati</option>
          <option value="lead">Lead</option>
          <option value="active">Attivi</option>
          <option value="inactive">Inattivi</option>
          <option value="archived">Archiviati</option>
        </select>
        <select className="input w-auto" value={clientType} onChange={(e) => { setClientType(e.target.value); setPage(1); }}>
          <option value="">Tutti i tipi</option>
          <option value="individual">Persone fisiche</option>
          <option value="company">Aziende</option>
        </select>
      </div>

      {error && <div className="p-3 rounded-md text-sm mb-4" style={{ background: 'rgba(239,68,68,.1)', color: '#ef4444' }}>{error}</div>}

      {/* Form inline */}
      {showForm && (
        <div className="card-elevated mb-4">
          <ClientForm clientId={editingId} onSaved={closeForm} onCancel={closeForm} />
        </div>
      )}

      {/* Tabella */}
      <div className="card-elevated overflow-x-auto">
        <table className="table-root">
          <thead>
            <tr>
              <th className="table-header">Cliente</th>
              <th className="table-header">Stato</th>
              <th className="table-header">Città</th>
              <th className="table-header">Email</th>
              <th className="table-header">Telefono</th>
              <th className="table-header text-right">Preventivi</th>
              <th className="table-header"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className="table-cell text-center py-8" colSpan={7}>Caricamento...</td></tr>
            )}
            {!loading && clients.length === 0 && (
              <tr><td className="table-cell text-center py-8" colSpan={7}>Nessun cliente trovato</td></tr>
            )}
            {!loading && clients.map((c) => (
              <tr key={c.id} className="table-row cursor-pointer transition-colors" onClick={() => navigate('/clients/' + c.id)}>
                <td className="table-cell">
                  <div className="font-medium text-surface">{c.display_name || c.legal_name}</div>
                  <div className="text-xs text-surface-4">{c.client_type === 'company' ? 'Azienda' : 'Persona fisica'}</div>
                </td>
                <td className="table-cell"><StatusBadge status={c.status} /></td>
                <td className="table-cell">{c.city}</td>
                <td className="table-cell text-brand-300">{c.email}</td>
                <td className="table-cell">{c.phone}</td>
                <td className="table-cell text-right font-mono">—</td>
                <td className="table-cell text-right">
                  <button
                    className="btn btn-ghost text-xs"
                    onClick={(e) => { e.stopPropagation(); setEditingId(c.id); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >
                    Modifica
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-line)' }}>
            <span className="text-xs text-surface-4">Pagina {page} di {totalPages}</span>
            <div className="flex gap-2">
              <button className="btn btn-secondary text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prec</button>
              <button className="btn btn-secondary text-xs" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Succ →</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

